
import { Component, Prop, Vue, Watch } from "vue-property-decorator";

import UPDATE_PROFILE_DETAILS from "@/apollo/profile/profileDetails/UpdateProfileDetail.gql";
import GET_PROFILE_DETAILS from "@/apollo/profile/profileDetails/GetProfileDetail.gql";
import GET_SKILLS from "@/apollo/autocomplete-queries/GetSkills.gql";
import GET_CONTRACTOR_PROFILE_DETAILS from "@/apollo/profile/profileDetails/GetContractorInfo.gql";
import FormWrapper from "@/components/form/FormWrapper.vue";
import FormTextarea from "@/components/form/FormTextarea.vue";
import FormGroup from "@/components/form/FormGroup.vue";
import UiButton from '@/components/button/ui-button.vue';
import SkillsMultiselect from "@/components/autocompleteMultiselects/SkillsMultiselectNew.vue";
import SoftSkillsMultiselect from "@/components/autocompleteMultiselects/SoftSkillsMultiselect.vue";
import IndustriesMultiselect from "@/components/autocompleteMultiselects/IndustriesMultiselect.vue";
import LanguagesMultiselect from "@/components/autocompleteMultiselects/LanguagesMultiselect.vue";
import TransportationMultiselect from "@/components/autocompleteMultiselects/TransportationMultiselect.vue";
import CurrencySelect from "@/components/autocompleteMultiselects/CurrencySelect.vue";
import RateUnitSelect from "@/components/autocompleteMultiselects/RateUnitSelect.vue";
import InputRangeFilter from "@/components/filters/InputRangeFilter.vue";

import {
  Industry,
  Language,
  User_Industry,
  User_Language,
  User_Profile,
  User_Skill,
} from "@/generated/graphql";
import { NOTIFICATION } from "@/entities/Events";
import Message from "@/data/notificationsMessages";
import UserResume from "@/entities/UserResume";

@Component({
  components: {
    FormWrapper,
    SkillsMultiselect,
    SoftSkillsMultiselect,
    IndustriesMultiselect,
    LanguagesMultiselect,
    TransportationMultiselect,
    CurrencySelect,
    RateUnitSelect,
    FormTextarea,
    FormGroup,
    UiButton,
    InputRangeFilter,
  },
  apollo: {
    profile: {
      query: GET_PROFILE_DETAILS,
      variables() {
        return {
          id: this.userId,
        };
      },
      update(data) {
        return data.user_profile_by_pk;
      },
    },
    userContractorDetails: {
      query: GET_CONTRACTOR_PROFILE_DETAILS,
      variables() {
        return {
          id: this.userId,
        };
      },
      update(data) {
        return data
      },
    },
  },
})
export default class ProfileDetail extends Vue {
  @Prop({ type: String, required: true }) readonly userId!: string;

  profile?: User_Profile = undefined;
  userContractorDetails: any = null;
  skillsIds: any[] = [];
  softSkillsIds: any[] = [];
  industries: Industry[] = [];
  languages: any[] = []; // Changed from Language[] to support proficiency_level
  transportation: any[] = [];
  maxAvailableHours: number | null = null;
  minRate: string = "";
  maxRate: string = "";
  currency: string = "";
  rateUnit: string = "";
  summary: string = "";
  contractorDetails: any = null;

  created() {
    this.$eventBus.listen(UserResume.UPDATE_RESUME, async (data: any) => {
      const scrappedResume = data.data
      if (scrappedResume) {
        if (scrappedResume.hasOwnProperty("summary")) {
          this.summary = scrappedResume.summary;
        }
        if (scrappedResume.hasOwnProperty("skills") && scrappedResume.skills) {
          const rawData = await this.$apollo.query({
            query: GET_SKILLS,
            variables: {
              where: {
                name: {
                  _in: scrappedResume.skills.map(s => s.name),
                },
              },
            },
          })
          this.skillsIds = rawData.data.skills_short_name_first.map(skill => skill.id)
        }
      }
    })
  }

  beforeDestroy() {
    this.$eventBus.remove(UserResume.UPDATE_RESUME)
  }

  @Watch("profile", { immediate: true })
  watchProfile(profile: User_Profile): void {
    if (!profile) return;
    this.skillsIds = profile.skills
      ?.filter(({ skill }) => skill && skill.id)
      .map(({ skill }) => skill.id) ?? [];
    this.softSkillsIds = profile.user_soft_skills
      ?.filter(({ soft_skill }) => soft_skill && soft_skill.id)
      .map(({ soft_skill }) => soft_skill.id) ?? [];

    this.industries = profile.industries?.map(({ industry }) => industry) ?? [];
    this.languages = profile.languages?.map(({ language, proficiency_level }) => ({
      ...language,
      proficiency_level: proficiency_level || 3 // Default to Conversational if not set
    })) ?? [];
    this.transportation = profile.transportation?.map(({ transportation_type }) => ({
      id: transportation_type,
      name: this.getTransportationDisplayName(transportation_type)
    })) ?? [];
    this.maxAvailableHours = profile.max_available_hours || null;
    this.minRate = profile.min_rate ?? "";
    this.maxRate = profile.max_rate ?? "";
    this.currency = profile.currency ?? "";
    this.rateUnit = profile.rate_unit ?? "";
    this.summary = profile.summary ?? "";
  }

  @Watch("userContractorDetails", { immediate: true })
  watchContractorDetails(details: any): void {
    if (!details || !details.contractor || details.contractor.length === 0) return;
    this.contractorDetails = details.contractor[0];
  }

  get companyId(): string | null {
    if (!this.userContractorDetails || !this.userContractorDetails.contractor || this.userContractorDetails.contractor.length === 0) {
      console.log('ProfileDetail: No contractor details available for companyId');
      return null;
    }
    const companyId = this.userContractorDetails.contractor[0].company_id || null;
    return companyId;
  }

  get userIndustries(): Partial<User_Industry>[] {
    return this.industries.map(({ id }) => ({
      user_id: this.userId,
      industry_id: id,
      added_by_id: this.userId,
    }));
  }

  get userSkills(): Partial<User_Skill>[] {
    return this.skillsIds.map(skillId => ({
      user_id: this.userId,
      skill_id: skillId,
      added_by_id: this.userId,
    }));
  }

  get userSoftSkills(): any[] {
    return this.softSkillsIds.map(softSkillId => ({
      user_id: this.userId,
      soft_skill_id: softSkillId,
      added_by_id: this.userId,
    }));
  }

  get userLanguages(): Partial<User_Language>[] {
    return this.languages.map(({ id, proficiency_level }) => ({
      user_id: this.userId,
      language_id: id,
      proficiency_level: proficiency_level || 3, // Default to Conversational
      added_by_id: this.userId,
    }));
  }

  get userTransportation(): any[] {
    return this.transportation.map(({ id }) => ({
      user_id: this.userId,
      transportation_type: id,
      added_by_id: this.userId,
    }));
  }

  get profileSet(): Partial<User_Profile> {
    return {
      min_rate: this.minRate || null,
      max_rate: this.maxRate || null,
      summary: this.summary || null,
      currency: this.currency || null,
      rate_unit: this.rateUnit || null,
      max_available_hours: this.maxAvailableHours || null,
    };
  }

  async updateProfileDetail(): Promise<void> {
    try {
      await this.update();

      this.$eventBus.emit(
        NOTIFICATION,
        Message.successMessage("Your background had been successfully updated.")
      );
      this.refetch();
    } catch (e) {
      this.$eventBus.emit(
        NOTIFICATION,
        Message.errorMessage(e.message)
      );
    }
  }

  async update(): Promise<void> {
    try {
      await this.$apollo.mutate({
        mutation: UPDATE_PROFILE_DETAILS,
        variables: {
          userId: this.userId,
          set: this.profileSet,
          industriesSet: this.userIndustries,
          skillsSet: this.userSkills,
          softSkillsSet: this.userSoftSkills,
          languagesSet: this.userLanguages,
          transportationSet: this.userTransportation,
        },
      });
    } catch (error) {
      console.log(error);
    }
  }

  refetch(): void {
    this.$apollo.queries.profile.refetch();
    this.$emit("update");
  }

  intercomNewMessage(): void {
    // @ts-ignore
    Intercom("showNewMessage", "Skills to add: ");
  }

  getTransportationDisplayName(transportationType: string): string {
    const displayNames: { [key: string]: string } = {
      'driving': 'Driving',
      'public transit': 'Public Transit',
      'cycling': 'Cycling',
      'walking': 'Walking'
    };
    return displayNames[transportationType] || transportationType;
  }
}
